import { Vector3 } from 'three';
import { TrackNode, makeNode } from './spline';

/** Where a template gets attached: position, travel direction, and local up. */
export interface EntryFrame {
  pos: Vector3;
  tan: Vector3; // unit, direction of travel
  up: Vector3; // unit, perpendicular to tan
}

/** Roll keys emitted by a template, as offsets (metres) from the insertion point. */
export interface RelRollKey {
  ds: number;
  deg: number;
  interp: 'smooth' | 'linear';
}

export interface TemplateResult {
  nodes: TrackNode[]; // world-space; nodes[0].p === entry.pos
  rollKeys: RelRollKey[];
  exit: EntryFrame;
  /** approximate arc length of the emitted run, for placing roll keys */
  length: number;
}

export type TemplateId =
  | 'straight'
  | 'verticalLoop'
  | 'corkscrew'
  | 'helix'
  | 'bankedTurn'
  | 'airtimeHill'
  | 'sBend';

/**
 * Turn a densely sampled analytic curve into Bézier nodes with tangent-aligned
 * handles (length = chord/3 — the standard smooth fit). Every ~`nodeEvery`-th
 * sample becomes a node. What lands in the document is ordinary draggable
 * control points; nothing is locked.
 */
function nodesFromSamples(points: Vector3[], nodeEvery: number): TrackNode[] {
  const idxs: number[] = [];
  for (let i = 0; i < points.length; i += nodeEvery) idxs.push(i);
  if (idxs[idxs.length - 1] !== points.length - 1) idxs.push(points.length - 1);

  const nodes: TrackNode[] = [];
  for (let k = 0; k < idxs.length; k++) {
    const i = idxs[k];
    const p = points[i];
    // central-difference tangent
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    const tan = next.clone().sub(prev).normalize();
    const dNext = k + 1 < idxs.length ? p.distanceTo(points[idxs[k + 1]]) : p.distanceTo(prev) * nodeEvery;
    const dPrev = k > 0 ? p.distanceTo(points[idxs[k - 1]]) : dNext;
    nodes.push(makeNode(p, tan.clone().multiplyScalar(-dPrev / 3), tan.clone().multiplyScalar(dNext / 3)));
  }
  return nodes;
}

function curveLength(points: Vector3[]): number {
  let L = 0;
  for (let i = 1; i < points.length; i++) L += points[i].distanceTo(points[i - 1]);
  return L;
}

/** Transport the entry up along the samples (projection method) to get a stable exit up. */
function exitFrameFrom(points: Vector3[], entryUp: Vector3): EntryFrame {
  const n = points.length;
  const up = entryUp.clone();
  for (let i = 1; i < n; i++) {
    const t = points[Math.min(i + 1, n - 1)].clone().sub(points[i - 1]).normalize();
    up.addScaledVector(t, -up.dot(t));
    if (up.lengthSq() < 1e-10) up.set(0, 1, 0).addScaledVector(t, -t.y);
    up.normalize();
  }
  const tan = points[n - 1].clone().sub(points[n - 2]).normalize();
  return { pos: points[n - 1].clone(), tan, up };
}

function frameBasis(entry: EntryFrame): { fwd: Vector3; up: Vector3; right: Vector3 } {
  const fwd = entry.tan.clone().normalize();
  const up = entry.up.clone().addScaledVector(fwd, -entry.up.dot(fwd)).normalize();
  const right = new Vector3().crossVectors(fwd, up).normalize();
  return { fwd, up, right };
}

/**
 * Fit a densely sampled curve into a template result — the entry point for
 * shapes assembled outside this module (see the demo's ramps and drops).
 */
export function fromSamples(
  points: Vector3[],
  entry: EntryFrame,
  nodeEvery: number,
  rollKeys: RelRollKey[] = [],
): TemplateResult {
  return result(points, entry, nodeEvery, rollKeys);
}

function result(points: Vector3[], entry: EntryFrame, nodeEvery: number, rollKeys: RelRollKey[] = []): TemplateResult {
  return {
    nodes: nodesFromSamples(points, nodeEvery),
    rollKeys,
    exit: exitFrameFrom(points, entry.up),
    length: curveLength(points),
  };
}

// ─── templates ──────────────────────────────────────────────────────────────

export function straight(entry: EntryFrame, params: { length?: number } = {}): TemplateResult {
  const len = params.length ?? 20;
  const { fwd } = frameBasis(entry);
  const points: Vector3[] = [];
  const n = Math.max(8, Math.round(len / 2));
  for (let i = 0; i <= n; i++) points.push(entry.pos.clone().addScaledVector(fwd, (len * i) / n));
  return result(points, entry, n); // just two nodes for a straight
}

/**
 * Clothoid (teardrop) vertical loop. Curvature ramps linearly up to 1/rTop,
 * holds, and ramps down, integrating θ(s) = ∫κ ds through a full 2π turn in
 * the vertical plane containing the entry tangent. Real loops are not circles —
 * a circular loop pulls brutal G at the bottom.
 */
export function verticalLoop(entry: EntryFrame, params: { height?: number } = {}): TemplateResult {
  const height = params.height ?? 20;
  // top radius and ramp length tuned so the integrated teardrop hits ~height
  const rTop = height * 0.46;
  const kMax = 1 / rTop;
  const sRamp = 2.0 * rTop; // clothoid ramp length
  const sConst = Math.max((2 * Math.PI - kMax * sRamp) / kMax, rTop * 0.5);
  const total = 2 * sRamp + sConst;

  const { fwd } = frameBasis(entry);
  const upW = new Vector3(0, 1, 0); // loops live in a vertical plane

  const n = 400;
  const ds = total / n;
  let theta = 0;
  const points: Vector3[] = [entry.pos.clone()];
  let x = 0;
  let y = 0;
  for (let i = 0; i < n; i++) {
    const s = (i + 0.5) * ds;
    let k: number;
    if (s < sRamp) k = (kMax * s) / sRamp;
    else if (s < sRamp + sConst) k = kMax;
    else k = (kMax * (total - s)) / sRamp;
    theta += k * ds;
    x += Math.cos(theta) * ds;
    y += Math.sin(theta) * ds;
    points.push(entry.pos.clone().addScaledVector(fwd, x).addScaledVector(upW, y));
  }
  // Inversion comes from RMF along the loop — chart 0° follows that frame.
  return result(points, entry, Math.round(n / 9));
}

/**
 * Corkscrew: the heartline follows a helix about a horizontal axis along the
 * direction of travel, with the radial term eased in and out so entry and exit
 * stay tangent-smooth. A corkscrew is defined by its roll rate, so it also
 * emits pinning roll keys.
 */
export function corkscrew(
  entry: EntryFrame,
  params: { radius?: number; length?: number; handed?: 1 | -1 } = {},
): TemplateResult {
  const r = params.radius ?? 5.5;
  const len = params.length ?? 30;
  const handed = params.handed ?? 1;
  const { fwd, up, right } = frameBasis(entry);

  const n = 400;
  const points: Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const phi = t * 2 * Math.PI;
    // ease the radius in over the first quarter and out over the last
    let env = 1;
    if (t < 0.25) env = smoothstep(t / 0.25);
    else if (t > 0.75) env = smoothstep((1 - t) / 0.25);
    const rr = r * env;
    const p = entry.pos
      .clone()
      .addScaledVector(fwd, len * t)
      .addScaledVector(up, rr * (1 - Math.cos(phi)))
      .addScaledVector(right, handed * rr * Math.sin(phi));
    points.push(p);
  }
  // Corkscrew inversion comes from RMF along the helix — chart 0° follows it.
  return result(points, entry, Math.round(n / 10));
}

/**
 * Flat-plan circular helix, climbing or descending, with auto bank keys.
 * `easeFrac` is the share of the run spent rolling into (and out of) full bank —
 * shorter means less time under-banked at the ends, at a higher roll rate.
 */
export function helix(
  entry: EntryFrame,
  params: {
    radius?: number;
    turns?: number;
    deltaHeight?: number;
    dir?: 1 | -1;
    bankDeg?: number;
    easeFrac?: number;
  } = {},
): TemplateResult {
  const r = params.radius ?? 12;
  const turns = params.turns ?? 1;
  const dh = params.deltaHeight ?? -6;
  const dir = params.dir ?? 1; // 1 = turn right
  const { fwd } = frameBasis(entry);
  const rightH = new Vector3(-fwd.z, 0, fwd.x).normalize().multiplyScalar(dir); // horizontal right

  const totalAngle = turns * 2 * Math.PI;
  const centre = entry.pos.clone().addScaledVector(rightH, r);
  const n = Math.max(60, Math.round(turns * 90));
  const points: Vector3[] = [];
  const fwdH = new Vector3(fwd.x, 0, fwd.z).normalize();
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = t * totalAngle;
    const radial = rightH
      .clone()
      .multiplyScalar(-Math.cos(a))
      .addScaledVector(fwdH, Math.sin(a));
    points.push(centre.clone().addScaledVector(radial, r).setY(entry.pos.y + dh * t));
  }
  const L = curveLength(points);
  const bank = (params.bankDeg ?? 45) * dir;
  const ease = params.easeFrac ?? 0.2;
  const rollKeys: RelRollKey[] = [
    { ds: 0, deg: 0, interp: 'smooth' },
    { ds: L * ease, deg: bank, interp: 'smooth' },
    { ds: L * (1 - ease), deg: bank, interp: 'smooth' },
    { ds: L, deg: 0, interp: 'smooth' },
  ];
  return result(points, entry, Math.round(n / (turns * 8)), rollKeys);
}

/** Banked flat turn through `angleDeg` at constant radius. */
export function bankedTurn(
  entry: EntryFrame,
  params: { radius?: number; angleDeg?: number; bankDeg?: number; dir?: 1 | -1; easeFrac?: number } = {},
): TemplateResult {
  const r = params.radius ?? 15;
  const angle = ((params.angleDeg ?? 180) * Math.PI) / 180;
  const dir = params.dir ?? 1;
  const bank = params.bankDeg ?? 40;
  const ease = params.easeFrac ?? 0.25;
  const res = helix(entry, { radius: r, turns: angle / (2 * Math.PI), deltaHeight: 0, dir });
  const rollKeys: RelRollKey[] = [
    { ds: 0, deg: 0, interp: 'smooth' },
    { ds: res.length * ease, deg: bank * dir, interp: 'smooth' },
    { ds: res.length * (1 - ease), deg: bank * dir, interp: 'smooth' },
    { ds: res.length, deg: 0, interp: 'smooth' },
  ];
  return { ...res, rollKeys };
}

/** Parabolic airtime hill: up and over with a cosine profile. */
export function airtimeHill(entry: EntryFrame, params: { length?: number; height?: number } = {}): TemplateResult {
  const len = params.length ?? 24;
  const h = params.height ?? 6;
  const { fwd } = frameBasis(entry);
  const fwdH = new Vector3(fwd.x, 0, fwd.z).normalize();
  const n = 100;
  const points: Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const y = h * 0.5 * (1 - Math.cos(2 * Math.PI * t));
    points.push(entry.pos.clone().addScaledVector(fwdH, len * t).add(new Vector3(0, y, 0)));
  }
  return result(points, entry, Math.round(n / 4));
}

/** S-bend: lateral offset with no net heading change. */
export function sBend(entry: EntryFrame, params: { length?: number; offset?: number } = {}): TemplateResult {
  const len = params.length ?? 24;
  const off = params.offset ?? 8;
  const { fwd } = frameBasis(entry);
  const fwdH = new Vector3(fwd.x, 0, fwd.z).normalize();
  const rightH = new Vector3(-fwdH.z, 0, fwdH.x);
  const n = 100;
  const points: Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const lateral = off * 0.5 * (1 - Math.cos(Math.PI * t));
    points.push(entry.pos.clone().addScaledVector(fwdH, len * t).addScaledVector(rightH, lateral));
  }
  return result(points, entry, Math.round(n / 4));
}

export function smoothstep(t: number): number {
  t = Math.min(Math.max(t, 0), 1);
  return t * t * (3 - 2 * t);
}

export const templates: Record<TemplateId, (entry: EntryFrame, params?: any) => TemplateResult> = {
  straight,
  verticalLoop,
  corkscrew,
  helix,
  bankedTurn,
  airtimeHill,
  sBend,
};
